import React from 'react';
import styled from '@emotion/styled';
import DatePicker, { registerLocale } from 'react-datepicker';
// eslint-disable-next-line import/no-duplicates
import { ru } from 'date-fns/locale';
// eslint-disable-next-line import/no-duplicates
import { isSameDay, setHours, setMinutes } from 'date-fns';
import { filter, upperFirst } from 'lodash-es';
import 'react-datepicker/dist/react-datepicker.css';
import { AxiosError } from 'axios';
import { Text } from '@chakra-ui/react';
import { Loader } from './Loader';
import { Paper } from './Paper';

registerLocale('ru', ru);

const getDayMessage = (day: Date) => {
  const messageString = day.toLocaleString('ru', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  });

  return messageString
    .split(' ')
    .map(upperFirst)
    .join(' ')
    .split(',')
    .reverse()
    .join(', ');
};

const splitTimes = (times: Date[] = []) => {
  const morning: Date[] = [];
  const afternoon: Date[] = [];
  const evening: Date[] = [];

  if (!times.length) {
    return { morning, afternoon, evening };
  }

  const noonHour = setHours(setMinutes(times[0], 0), 12);
  const eveningHour = setHours(setMinutes(times[0], 0), 18);

  times.forEach((time: Date) => {
    if (time < eveningHour) {
      if (time < noonHour) {
        morning.push(time);
        return;
      }

      afternoon.push(time);
      return;
    }

    evening.push(time);
  });

  return { morning, afternoon, evening };
};

const ContainerStyled = styled.div`
  position: relative;
  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    min-height: 300px;
  }

  .react-datepicker {
    font-family: inherit;
    background: transparent;
    border-color: transparent;
    font-size: 14px;
    width: 100%;

    .react-datepicker__header {
      display: none;
    }

    .react-datepicker__time-container {
      width: 100%;
      .react-datepicker__time {
        position: static;
        background: transparent;

        .react-datepicker__time-box {
          width: auto;
          overflow: unset;

          .react-datepicker__time-list {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            height: auto;
            overflow: unset;
            gap: 8px 12px;
            @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
              gap: 8px;
            }
            .react-datepicker__time-list-item {
              display: flex;
              justify-content: center;
              align-items: center;
              background: ${({ theme }) => theme.colors.paper};
              border-radius: 12px;
              flex: 0 0 85px;
              height: 50px;

              @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
                width: 64px;
                height: 32px;
              }

              &:hover {
                background: ${({ theme }) => theme.colors.highlight};
                color: ${({ theme }) => theme.colors.white};
              }

              &.react-datepicker__time-list-item--selected {
                background: ${({ theme }) => theme.colors.highlight};
                color: ${({ theme }) => theme.colors.white};
              }

              &.react-datepicker__time-list-item--disabled {
                display: none;
              }
            }
          }
        }
      }
    }
  }
`;

interface TimesMapperProps {
  selected: Date;
  onChange: (date: Date) => void;
  times: Date[];
}

const TimesMapper: React.FC<TimesMapperProps> = ({
  selected,
  onChange,
  times,
}) => {
  return (
    <DatePicker
      inline
      disabledKeyboardNavigation
      showTimeSelectOnly
      showTimeSelect
      locale="ru"
      selected={selected}
      timeIntervals={1}
      onChange={(date) => {
        onChange(date as Date);
      }}
      includeTimes={times}
    />
  );
};

interface Props {
  loading?: boolean;
  error?: AxiosError<unknown>;
  value: Date;
  selectedDay: Date;
  onChange: (date: Date) => void;
  include?: Date[];
}

export const TimePicker: React.FC<Props> = ({
  loading,
  error,
  value,
  onChange,
  selectedDay,
  include,
}) => {
  const now = new Date();

  const times = isSameDay(selectedDay, now)
    ? filter(include, (time) => {
        const timeValue = time.getHours() * 60 + time.getMinutes();
        const nowValue = now.getHours() * 60 + now.getMinutes();

        return nowValue < timeValue;
      })
    : include;

  const { morning, afternoon, evening } = splitTimes(times);

  return (
    <Paper>
      <ContainerStyled>
        <Loader loading={loading} error={error} />

        <Text textStyle="h4" align="center" m={3}>
          {getDayMessage(selectedDay)}
        </Text>
        {!!morning.length && (
          <>
            <Text textStyle="h4" my={3}>
              Утро
            </Text>
            <TimesMapper selected={value} onChange={onChange} times={morning} />
          </>
        )}

        {!!afternoon.length && (
          <>
            <Text textStyle="h4" my={3}>
              День
            </Text>
            <TimesMapper
              selected={value}
              onChange={onChange}
              times={afternoon}
            />
          </>
        )}

        {!!evening.length && (
          <>
            <Text textStyle="h4" my={3}>
              Вечер
            </Text>
            <TimesMapper selected={value} onChange={onChange} times={evening} />
          </>
        )}
      </ContainerStyled>
    </Paper>
  );
};
